import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { usePlaidLink } from "react-plaid-link";
import { Landmark, Plus, RefreshCw, Trash2, Loader2, ShieldCheck, Sparkles } from "lucide-react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";

/**
 * Plaid-powered bank & brokerage connections page.
 *
 * Flow: click "Connect a bank" → we hit /plaid/link/token → open Plaid Link
 * with the returned token → onSuccess we POST /plaid/item with the
 * public_token → backend exchanges it, kicks off transaction + investment
 * sync in the background, and we refresh the list.
 *
 * Sandbox test creds: username "user_good", password "pass_good".
 */
export default function Connections() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linkToken, setLinkToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [syncingId, setSyncingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/plaid/items");
      setItems(data || []);
    } catch (e) {
      console.error("Load plaid items failed:", e);
      toast.error(t("pages.connections.toast_bank_conn_load_failed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  // Fetch a link token BEFORE opening Plaid (Plaid needs it up-front)
  const openPlaid = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/plaid/link/token");
      setLinkToken(data.link_token);
    } catch (e) {
      console.error("Link token creation failed:", e);
      toast.error(t("pages.connections.toast_plaid_start_failed"));
      setBusy(false);
    }
  };

  const onSuccess = useCallback(async (public_token, metadata) => {
    try {
      const { data } = await api.post("/plaid/item", {
        public_token,
        institution_id: metadata?.institution?.institution_id || null,
        institution_name: metadata?.institution?.name || null,
      });
      toast.success(t("pages.connections.toast_bank_connected", { name: data.institution_name }));
      // Give the background sync a moment to insert rows, then refresh
      setTimeout(load, 1500);
    } catch (e) {
      console.error("Item exchange failed:", e);
      toast.error(t("pages.connections.toast_bank_save_failed"));
    } finally {
      setLinkToken(null);
      setBusy(false);
    }
  }, [load, t]);

  const onExit = useCallback((err) => {
    if (err) console.warn("Plaid Link exited with error:", err);
    setLinkToken(null);
    setBusy(false);
  }, []);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
    onExit,
  });

  // Auto-open the moment the token is ready
  useEffect(() => {
    if (linkToken && ready) open();
  }, [linkToken, ready, open]);

  const manualSync = async (item_id) => {
    setSyncingId(item_id);
    try {
      const { data } = await api.post(`/plaid/items/${item_id}/sync`);
      toast.success(t("pages.connections.toast_sync_done", { tx: data.transactions_added || 0, holdings: data.holdings_upserted || 0 }));
      load();
    } catch (e) {
      console.error("Manual sync failed:", e);
      toast.error(t("pages.connections.toast_sync_failed"));
    } finally {
      setSyncingId(null);
    }
  };

  const disconnect = async (item_id, name) => {
    const wipe = confirm(t("pages.connections.confirm_wipe", { name }));
    // If they cancelled the wipe question, ask if they still want to disconnect (preserve history)
    if (!wipe) {
      if (!confirm(t("pages.connections.confirm_keep", { name }))) return;
    }
    try {
      const { data } = await api.delete(
        `/plaid/items/${item_id}${wipe ? "?purge=true" : ""}`
      );
      const purged = data?.purged || {};
      const purgeMsg = wipe && (purged.expenses || purged.investments)
        ? ` · purged ${purged.expenses || 0} tx + ${purged.investments || 0} holdings`
        : "";
      toast.success(t("pages.connections.toast_disconnected", { name, purge: purgeMsg }));
      load();
    } catch (e) {
      console.error("Disconnect failed:", e);
      toast.error(t("pages.connections.toast_disconnect_failed"));
    }
  };

  return (
    <div data-testid="connections-page">
      <PageHeader
        subtitle={t("pages.connections.subtitle")}
        title={t("pages.connections.title")}
      />

      {/* Intro / trust block */}
      <div className="card-flat p-6 mb-5">
        <div className="flex items-start gap-4">
          <div className="h-11 w-11 rounded-xl bg-[var(--bg-accent)] flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="text-[var(--accent-primary)]" size={22} />
          </div>
          <div className="flex-1">
            <div className="text-[15px] font-medium text-[var(--text-primary)] mb-1">
              {t("pages.connections.trust_title")}
            </div>
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
              {t("pages.connections.trust_body_1")}
              <span className="font-mono text-xs px-1.5 py-0.5 mx-1 rounded bg-[var(--bg-accent)]">user_good</span>
              /
              <span className="font-mono text-xs px-1.5 py-0.5 mx-1 rounded bg-[var(--bg-accent)]">pass_good</span>
              {t("pages.connections.trust_body_2")}
            </p>
          </div>
          <button
            className="btn-primary flex items-center gap-2"
            onClick={openPlaid}
            disabled={busy}
            data-testid="connections-connect-btn"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
            {busy ? t("pages.connections.opening") : t("pages.connections.connect_bank")}
          </button>
        </div>
      </div>

      {/* Connected items */}
      {loading ? (
        <div className="card-flat p-10 text-center text-sm text-[var(--text-muted)]">
          {t("pages.connections.loading_connections")}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          title={t("pages.connections.empty_title")}
          body={t("pages.connections.empty_body_long")}
          action={
            <button
              className="btn-primary flex items-center gap-2"
              onClick={openPlaid}
              disabled={busy}
              data-testid="connections-empty-connect-btn"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
              {t("pages.connections.connect_bank")}
            </button>
          }
        />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((it) => (
            <div key={it.item_id} className="card-flat p-5" data-testid={`connection-${it.item_id}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="h-10 w-10 rounded-lg bg-[var(--bg-accent)] flex items-center justify-center flex-shrink-0">
                  <Landmark className="text-[var(--accent-primary)]" size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[15px] font-medium text-[var(--text-primary)] truncate">
                    {it.institution_name || "Bank"}
                  </div>
                  <div className="text-[11px] text-[var(--text-muted)] font-mono truncate">
                    {t("pages.connections.item_prefix")} {it.item_id.slice(0, 14)}…
                  </div>
                </div>
                <StatusPill status={it.status} t={t} />
              </div>

              <div className="text-xs text-[var(--text-secondary)] mb-4 flex items-center gap-1.5">
                <Sparkles size={12} className="text-[var(--accent-primary)]" />
                {t("pages.connections.last_synced", { when: formatTs(it.last_synced_at, t) })}
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  className="btn-ghost text-sm flex items-center gap-1.5"
                  onClick={() => manualSync(it.item_id)}
                  disabled={syncingId === it.item_id}
                  data-testid={`connection-sync-${it.item_id}`}
                >
                  {syncingId === it.item_id
                    ? <Loader2 size={14} className="animate-spin" />
                    : <RefreshCw size={14} />}
                  {syncingId === it.item_id ? t("pages.connections.syncing_a") : t("pages.connections.sync_now")}
                </button>
                <button
                  className="btn-ghost text-sm flex items-center gap-1.5"
                  onClick={() => disconnect(it.item_id, it.institution_name || "This bank")}
                  data-testid={`connection-disconnect-${it.item_id}`}
                  style={{ color: "var(--text-muted)" }}
                >
                  <Trash2 size={14} />
                  {t("pages.connections.disconnect")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status, t }) {
  const map = {
    ready:   { key: "status_ready",   bg: "rgba(123,196,144,0.18)", fg: "#2D5A40" },
    syncing: { key: "status_syncing", bg: "rgba(224,158,80,0.20)",  fg: "#8A5A18" },
    error:   { key: "status_error",   bg: "rgba(200,80,80,0.20)",   fg: "#8A2222" },
  };
  const s = map[status] || map.syncing;
  return (
    <span
      className="text-[10px] font-medium tracking-wider uppercase px-2 py-0.5 rounded-full"
      style={{ background: s.bg, color: s.fg }}
    >
      {t(`pages.connections.${s.key}`)}
    </span>
  );
}

function formatTs(ts, t) {
  if (!ts) return t("pages.connections.when_never");
  try {
    const d = new Date(ts);
    const diff = Date.now() - d.getTime();
    const mins = Math.round(diff / 60000);
    if (mins < 1) return t("pages.connections.when_just_now");
    if (mins < 60) return t("pages.connections.when_mins", { count: mins });
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return t("pages.connections.when_hrs", { count: hrs });
    const days = Math.round(hrs / 24);
    return t("pages.connections.when_days", { count: days });
  } catch { return ts; }
}
